import type { OpportunityRelatedDeal } from '~/hooks/apis/offers/useGetOpportunityRelated';

import { formatDateShortDD, formatDateWithClock, isValidDateString } from '~/services/TimeService';

import currencyFormatter from '~/utils/currencyFormatter';

// currencyFormatter falls back to AUD when the currency is undefined, which would misprice a
// synced value, so an unknown currency is passed as null to render an empty cell instead.
export function formatSyncedMoney(currency?: string | null, amount?: number | string | null) {
  if (amount === null || amount === undefined || amount === '') {
    return undefined;
  }

  const numericAmount = Number(amount);

  if (!Number.isFinite(numericAmount)) {
    return undefined;
  }

  return currencyFormatter(currency ?? null, numericAmount, 2, 'code');
}

export function getUniqueRoomAssets(deals?: Array<OpportunityRelatedDeal>) {
  const seenSfids = new Set<string>();

  return (deals ?? []).reduce<Array<NonNullable<OpportunityRelatedDeal['room_asset']>>>((roomAssets, deal) => {
    const roomAsset = deal.room_asset;

    if (!roomAsset?.sfid || seenSfids.has(roomAsset.sfid)) {
      return roomAssets;
    }

    seenSfids.add(roomAsset.sfid);
    roomAssets.push(roomAsset);
    return roomAssets;
  }, []);
}

export function formatSyncedDate(value?: string | null) {
  if (!value) {
    return undefined;
  }

  return isValidDateString(value) ? formatDateWithClock(value) : value;
}

export function formatSyncedDay(value?: string | null) {
  if (!value) {
    return undefined;
  }

  return isValidDateString(value) ? formatDateShortDD(value) : value;
}

export function salesforceRecordUrl(objectName: string, recordId?: string | null) {
  const host = window.configs.SALESFORCE_HOST;

  return host && recordId ? `${host}/lightning/r/${objectName}/${recordId}/view` : undefined;
}

// Salesforce returns multi-select picklists as a semicolon-delimited string. Single-select values
// pass through unchanged.
export function formatPicklistValues(value?: string | null) {
  if (!value) {
    return undefined;
  }

  return value
    .split(';')
    .map((entry) => entry.trim())
    .filter(Boolean)
    .join(', ');
}

export function formatCreatedOrModifiedBy(name?: string | null, when?: string | null) {
  const formattedWhen = formatSyncedDate(when);

  if (!name && !formattedWhen) {
    return undefined;
  }

  return [name, formattedWhen].filter(Boolean).join(', ');
}
